import { useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { getHolidayName } from '@bhops/shared';
import type { ProductionPlan } from '@/api/production-plans';
import { Icon } from '@/components/Icon';
import { kstYmd } from '@/lib/format-date';

// docs/work_log/v1202-followups-batch-2 §1 — ProductionPlan calendar + Gantt.
// 영업 ETA 조회의 시각화 — InstallCalendarPage 와 동일 native-grid 패턴.

const STATUS_COLOR: Record<string, string> = {
  PLANNED: 'var(--fg-muted)',
  IN_PROGRESS: 'var(--blue-600)',
  COMPLETED: 'var(--robot-operating)',
  PARTIALLY_COMPLETED: 'var(--dday-warning)',
  DELAYED: 'var(--severity-urgent)',
  CANCELLED: 'var(--fg-disabled)',
};
const STATUS_LABEL: Record<string, string> = {
  PLANNED: '계획',
  IN_PROGRESS: '진행 중',
  COMPLETED: '완성',
  PARTIALLY_COMPLETED: '부분 완성',
  DELAYED: '지연',
  CANCELLED: '취소',
};

function calendarCells(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const cells: Date[] = [];
  const startOffset = firstDay.getDay();
  const totalDays = lastDay.getDate();
  for (let i = startOffset - 1; i >= 0; i--) cells.push(new Date(year, month, -i));
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));
  const trail = (7 - (cells.length % 7)) % 7;
  for (let i = 1; i <= trail; i++) cells.push(new Date(year, month + 1, i));
  return cells;
}

// R2 후속(워크플로 M16) — 로컬 기준 YYYY-MM-DD. toISOString() 은 UTC 라 KST(UTC+9)
// 에서 로컬 자정 셀이 전날로 밀려 계획 칩이 하루 어긋나 표시됐다. 셀 키와 plansByDay
// 키를 이 로컬 ymd 로 통일해 같은 날짜 셀에 칩이 들어가게 한다.
function localYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// 추가수정 2차(26-07-27) 정정5 — 화면 표시용 점 표기 "YYYY.MM.DD".
//
// 간트 헤더의 표시 기간 라벨만 toLocaleDateString('ko-KR') 이라 "2026. 7. 27." 로
// 나와, 같은 화면 막대 툴팁의 "2026.07.27"(정정3에서 kstYmd 로 통일) 과 표기가
// 달랐다. 바꾸는 것은 **구분자(표기)뿐**이다.
//
// ⚠ 라벨에 kstYmd 를 쓰면 안 된다. 간트의 startDate/endDate 는 브라우저 로컬
//   자정 기준 Date 이고 일자 셀(축)·공휴일 키와 같은 축이다. 이 값을 kstYmd 에
//   넣으면 로컬 축 값을 KST 로 한 번 더 변환하게 되는데, 로컬 오프셋이 UTC+9 를
//   넘는 지역(UTC+9:30 애들레이드, UTC+10 시드니, UTC+12 오클랜드)에서 라벨만
//   전날로 밀려 축과 어긋난다(실측 확인). 그래서 축 값(localYmd) 을 그대로 쓰고
//   하이픈만 점으로 바꾼다 — 라벨은 정의상 축과 절대 어긋나지 않는다.
function localYmdDot(d: Date): string {
  return localYmd(d).replace(/-/g, '.');
}

const cellBase: CSSProperties = {
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  minHeight: 100,
  padding: 6,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  fontSize: 11,
};
const cellMuted: CSSProperties = { ...cellBase, background: 'var(--bg-disabled)' };
const chipBase: CSSProperties = {
  padding: '2px 5px',
  borderRadius: 3,
  fontSize: 10,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textDecoration: 'none',
  display: 'block',
  color: 'var(--fg-inverse)',
};
const navBtn: CSSProperties = {
  padding: '6px 12px',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
};

export function ProductionPlanCalendar({ plans }: { plans: ProductionPlan[] }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  const cells = useMemo(() => calendarCells(year, month), [year, month]);
  const plansByDay = useMemo(() => {
    const map = new Map<string, ProductionPlan[]>();
    for (const p of plans) {
      const key = localYmd(new Date(p.estimatedCompletionAt));
      const list = map.get(key) ?? [];
      list.push(p);
      map.set(key, list);
    }
    return map;
  }, [plans]);

  const prev = () => {
    if (month === 0) {
      setYear(year - 1);
      setMonth(11);
    } else setMonth(month - 1);
  };
  const next = () => {
    if (month === 11) {
      setYear(year + 1);
      setMonth(0);
    } else setMonth(month + 1);
  };
  const today = () => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth());
  };

  const dayHeaders = ['일', '월', '화', '수', '목', '금', '토'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={prev} style={navBtn}>
          <Icon name="chev-left" size={16} aria-hidden />
        </button>
        <div style={{ fontSize: 18, fontWeight: 700, minWidth: 140, textAlign: 'center' }}>
          {year}년 {month + 1}월
        </div>
        <button onClick={next} style={navBtn}>
          <Icon name="chev-right" size={16} aria-hidden />
        </button>
        <button onClick={today} style={{ ...navBtn, fontWeight: 600 }}>
          오늘
        </button>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11 }}>
        {Object.entries(STATUS_LABEL).map(([k, v]) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[k] }} />
            {v}
          </span>
        ))}
      </div>

      {/* Day headers */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          fontSize: 10,
          color: 'var(--fg-muted)',
          textTransform: 'uppercase',
        }}
      >
        {dayHeaders.map((d, i) => (
          <div
            key={d}
            style={{
              padding: '4px 8px',
              color: i === 0 ? 'var(--fg-danger)' : i === 6 ? 'var(--report-assigned)' : 'inherit',
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {cells.map((cell, idx) => {
          const isCurrentMonth = cell.getMonth() === month;
          const dateKey = localYmd(cell);
          const events = plansByDay.get(dateKey) ?? [];
          const holiday = getHolidayName(dateKey); // round10 B2 — 공휴일명(대체공휴일 포함)
          const isToday = cell.toDateString() === new Date().toDateString();
          return (
            <div
              key={`${idx}-${dateKey}`}
              style={{
                ...(isCurrentMonth ? cellBase : cellMuted),
                ...(isToday ? { outline: '2px solid var(--border-focus)', outlineOffset: -1 } : {}),
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  fontWeight: isToday ? 700 : 400,
                  color: isCurrentMonth
                    ? holiday || cell.getDay() === 0
                      ? 'var(--fg-danger)'
                      : cell.getDay() === 6
                        ? 'var(--report-assigned)'
                        : 'inherit'
                    : 'var(--fg-disabled)',
                }}
              >
                <span>{cell.getDate()}</span>
                {events.length > 0 && (
                  <span
                    style={{
                      fontSize: 9,
                      background: 'var(--bg-accent-subtle)',
                      padding: '0 4px',
                      borderRadius: 4,
                      color: 'var(--fg-brand)',
                    }}
                  >
                    {events.length}
                  </span>
                )}
              </div>
              {/* round10 B2 — 공휴일명(대체공휴일 포함). 셀 자체 날짜 기준. */}
              {holiday && isCurrentMonth && (
                <div
                  title={holiday}
                  style={{
                    fontSize: 9,
                    color: 'var(--fg-danger)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {holiday}
                </div>
              )}
              {events.slice(0, 3).map((e) => (
                <Link
                  key={e.id}
                  to="/manufacturing/production-plan"
                  style={{
                    ...chipBase,
                    background: STATUS_COLOR[e.status] ?? 'var(--fg-muted)',
                  }}
                  title={`${e.humanId} · ${e.model?.displayName ?? e.modelId} · ${e.plannedCount}대 · 가용 ${e.availableCount ?? '–'}`}
                >
                  {e.model?.code ?? '?'} ({e.plannedCount}, 가용 {e.availableCount ?? '–'})
                </Link>
              ))}
              {events.length > 3 && (
                <div style={{ fontSize: 10, color: 'var(--fg-muted)' }}>
                  +{events.length - 3} 더 보기
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Timeline view — 가로 막대(간트) 차트
// ───────────────────────────────────────────────────────────────

// R2 후속 #3 — 라벨열은 2줄(humanId + 모델·수량). 행 높이 32px 는 패딩 포함 시
// 2줄을 못 담아 둘째 줄(예: "GEN2 · 5대")이 하단에서 짤렸다. 44px 로 키워 2줄을
// 온전히 표시(막대는 top:6 + height:rowH-12 로 자동 중앙정렬되어 비례 유지).
const ganttRowH = 44;
const ganttDayW = 28;
const ganttLabelW = 200;

// Round 2 Phase F (#8) — 라벨열 공통 스타일. 단일 가로 스크롤 컨테이너 안에서
// position:sticky; left:0 으로 고정돼, 타임라인을 가로 스크롤해도 Plan 식별
// 정보가 항상 보인다. 헤더/행이 같은 스크롤 박스를 공유하므로 열이 어긋나지
// 않는다(기존엔 헤더와 본문이 독립 overflow:auto 라 스크롤 시 desync).
const ganttLabelCell: CSSProperties = {
  width: ganttLabelW,
  flexShrink: 0,
  padding: '4px 8px',
  fontSize: 11,
  position: 'sticky',
  left: 0,
  background: 'var(--bg-surface)',
  zIndex: 1,
};

export function ProductionPlanGantt({ plans }: { plans: ProductionPlan[] }) {
  const now = new Date();
  const [weeks, setWeeks] = useState(4);
  const [anchorOffset, setAnchorOffset] = useState(0); // weeks from today

  const startDate = useMemo(() => {
    const d = new Date(now.getTime() + anchorOffset * 7 * 24 * 60 * 60_000);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [anchorOffset]);
  const totalDays = weeks * 7;
  const endDate = useMemo(
    () => new Date(startDate.getTime() + totalDays * 24 * 60 * 60_000),
    [startDate, totalDays],
  );
  const timelineW = totalDays * ganttDayW;

  // 표시 대상: ETA 가 range 와 겹치는 plan (CANCELLED 제외)
  const visiblePlans = useMemo(() => {
    return plans
      .filter((p) => p.status !== 'CANCELLED')
      .filter((p) => {
        const eta = new Date(p.estimatedCompletionAt);
        return eta >= new Date(startDate.getTime() - 14 * 24 * 60 * 60_000) && eta <= endDate;
      })
      .sort(
        (a, b) =>
          new Date(a.estimatedCompletionAt).getTime() - new Date(b.estimatedCompletionAt).getTime(),
      );
  }, [plans, startDate, endDate]);

  // 각 plan 의 bar: 시작 (ETA - 7일) → 끝 (ETA + 1d). 7일 lead-time 가정.
  // Round 2 Phase F (#8) — px 좌표를 트랙 [0, timelineW] 로 clamp 한다. 기존엔
  // 음수 leftDays(ETA 가 윈도 시작 근처) 가 막대를 좌측으로 오버플로시켰다.
  // 윈도와 전혀 겹치지 않으면 width<=0 → 막대를 그리지 않는다.
  function barFor(p: ProductionPlan): { left: number; width: number } {
    const eta = new Date(p.estimatedCompletionAt);
    const barStart = new Date(eta.getTime() - 7 * 24 * 60 * 60_000);
    const leftDays = (barStart.getTime() - startDate.getTime()) / (24 * 60 * 60_000);
    const rawLeft = leftDays * ganttDayW;
    const rawRight = (leftDays + 8) * ganttDayW;
    const left = Math.max(0, Math.min(timelineW, rawLeft));
    const right = Math.max(0, Math.min(timelineW, rawRight));
    return { left, width: right - left };
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => setAnchorOffset(anchorOffset - 1)} style={navBtn}>
          <Icon name="chev-left" size={14} aria-hidden /> 1주
        </button>
        <button onClick={() => setAnchorOffset(0)} style={{ ...navBtn, fontWeight: 600 }}>
          오늘
        </button>
        <button onClick={() => setAnchorOffset(anchorOffset + 1)} style={navBtn}>
          1주 <Icon name="chev-right" size={14} aria-hidden />
        </button>
        <select
          value={weeks}
          onChange={(e) => setWeeks(Number(e.target.value))}
          // round5 #21 — UA 기본 흰 배경이 다크에서 떠 보임.
          style={{
            fontSize: 12,
            padding: '4px 8px',
            background: 'var(--bg-surface)',
            color: 'var(--fg-default)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-input)',
          }}
        >
          <option value={2}>2주</option>
          <option value={4}>4주</option>
          <option value={8}>8주</option>
          <option value={12}>12주</option>
        </select>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--fg-muted)' }}>
          {/* 추가수정 2차(26-07-27) 정정5 — 표시 기간 라벨 표기를 막대 툴팁과 같은
              점 표기로. 축 값(로컬 자정 Date) 은 그대로, 구분자만 바뀐다. */}
          {localYmdDot(startDate)} ~ {localYmdDot(new Date(endDate.getTime() - 24 * 60 * 60_000))}
          {' · '}계획 {visiblePlans.length}건
        </div>
      </div>

      {/* 단일 가로 스크롤 컨테이너 — 헤더와 행이 같은 스크롤 박스를 공유해
          가로 스크롤 시에도 날짜열과 막대가 정렬을 유지한다. 라벨열은
          sticky 로 좌측 고정. */}
      <div
        style={{
          overflowX: 'auto',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          background: 'var(--bg-surface)',
        }}
      >
        <div style={{ minWidth: ganttLabelW + timelineW }}>
          {/* Header: dates */}
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid var(--border-subtle)',
              background: 'var(--bg-surface)',
            }}
          >
            <div style={{ ...ganttLabelCell, color: 'var(--fg-muted)', zIndex: 3 }}>Plan</div>
            <div style={{ display: 'flex', width: timelineW }}>
              {Array.from({ length: totalDays }, (_, i) => {
                const day = new Date(startDate.getTime() + i * 24 * 60 * 60_000);
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const holiday = getHolidayName(localYmd(day)); // round10 B2
                const isToday = day.toDateString() === new Date().toDateString();
                return (
                  <div
                    key={i}
                    title={holiday || undefined}
                    style={{
                      width: ganttDayW,
                      flexShrink: 0,
                      padding: '2px 0',
                      textAlign: 'center',
                      fontSize: 9,
                      borderLeft: '1px solid var(--border-subtle)',
                      background: isToday
                        ? 'var(--bg-info-subtle)'
                        : isWeekend || holiday
                          ? 'var(--bg-disabled)'
                          : undefined,
                      color:
                        holiday || day.getDay() === 0
                          ? 'var(--fg-danger)'
                          : day.getDay() === 6
                            ? 'var(--report-assigned)'
                            : 'inherit',
                      fontWeight: isToday ? 700 : 400,
                    }}
                  >
                    <div>{day.getDate()}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rows */}
          {visiblePlans.length === 0 && (
            <div
              style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}
            >
              해당 기간에 계획이 없습니다.
            </div>
          )}
          {visiblePlans.map((p) => {
            const bar = barFor(p);
            const color = STATUS_COLOR[p.status] ?? 'var(--fg-muted)';
            const fillPercent = Math.max(0, Math.min(100, p.progressPercent ?? 0));
            return (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  height: ganttRowH,
                  borderTop: '1px solid var(--border-subtle)',
                }}
              >
                <div
                  style={{
                    ...ganttLabelCell,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    borderRight: '1px solid var(--border-subtle)',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {p.humanId}
                  </div>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 10 }}>
                    {p.model?.code ?? '?'} · {p.plannedCount}대
                  </div>
                </div>
                <div
                  style={{
                    width: timelineW,
                    flexShrink: 0,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      pointerEvents: 'none',
                    }}
                  >
                    {Array.from({ length: totalDays }, (_, i) => (
                      <div
                        key={i}
                        style={{
                          width: ganttDayW,
                          flexShrink: 0,
                          borderLeft: '1px solid var(--border-subtle)',
                        }}
                      />
                    ))}
                  </div>
                  {bar.width > 0 && (
                    <div
                      // 추가수정 2차(26-07-27) 정정3 — 툴팁 ETA **표기만** 콘솔 표준
                      // (점 표기)으로 통일. toLocaleDateString 의 "2026. 7. 27." 는
                      // 다른 화면과 모양이 달랐다. 막대 위치·필터·간트 축 라벨은
                      // 브라우저 로컬 축으로 계산되므로 그대로 둔다(축·라벨 정합).
                      title={`${p.humanId} · ETA ${kstYmd(p.estimatedCompletionAt) ?? '-'} · ${p.plannedCount}대 · ${fillPercent}%`}
                      style={{
                        position: 'absolute',
                        left: bar.left,
                        top: 6,
                        width: bar.width,
                        height: ganttRowH - 12,
                        background: 'var(--bg-active)',
                        borderRadius: 4,
                        overflow: 'hidden',
                        border: `1px solid ${color}`,
                      }}
                    >
                      <div
                        style={{
                          width: `${fillPercent}%`,
                          height: '100%',
                          background: color,
                          transition: 'width 0.2s',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 9,
                          fontWeight: 600,
                          color: fillPercent > 50 ? 'var(--fg-inverse)' : color,
                          pointerEvents: 'none',
                        }}
                      >
                        {fillPercent}%
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="kuun-caption" style={{ color: 'var(--fg-muted)', fontSize: 11 }}>
        막대는 완성 예정일 7일 전부터 1일 뒤까지입니다. 채워진 부분은 진행률이고, 색은 상태를
        나타냅니다.
      </div>
    </div>
  );
}
